import axios, { AxiosInstance, CancelTokenSource } from 'axios';
import { Config } from '../../config';
import { IErrorHandler } from '../ErrorHandler/IErrorHandler';
import {
  DeleteRequestConfig,
  GetRequestConfig,
  IHttpClient,
  PatchRequestConfig,
  PostRequestConfig,
  PutRequestConfig,
} from './IHttpClient';

export type HttpClientInitParams = {
  errorHandler: IErrorHandler;
  axiosInstance?: AxiosInstance;
};

export class CognitoHttpClient implements IHttpClient {
  private readonly axiosInstance: AxiosInstance;
  private readonly cancelToken: CancelTokenSource;
  private errorInterceptor: null | number = null;

  constructor({ axiosInstance, errorHandler }: HttpClientInitParams) {
    this.cancelToken = axios.CancelToken.source();
    this.axiosInstance =
      axiosInstance ??
      axios.create({
        baseURL: `https://cognito-idp.${Config.cognitoRegion}.amazonaws.com/`,
        cancelToken: this.cancelToken.token,
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
        },
        timeout: 30000,
      });

    this.setupErrorHandler(errorHandler);
  }

  public get = async <T>(
    url: string,
    configs?: GetRequestConfig
  ): Promise<T> => {
    const res = await this.axiosInstance.get<T>(url, {
      params: configs?.params,
      headers: configs?.headers,
    });
    return res.data;
  };

  public post = async <T, P>(
    url: string,
    configs?: PostRequestConfig<P>
  ): Promise<T> => {
    const res = await this.axiosInstance.post<T>(url, configs?.body, {
      headers: configs?.headers,
    });
    return res.data;
  };

  public put = async <T, P>(
    url: string,
    configs: PutRequestConfig<P> = { isAuthorized: true }
  ): Promise<T> => {
    const res = await this.axiosInstance.put<T>(url, configs?.body, {
      headers: configs?.headers,
      data: configs?.data,
    });

    return res.data;
  };

  public patch = async <T, P>(
    url: string,
    configs?: PatchRequestConfig<P>
  ): Promise<T> => {
    const res = await this.axiosInstance.patch<T>(url, configs?.body, {
      headers: configs?.headers,
    });

    return res.data;
  };

  public delete = async <T, P = any>(
    url: string,
    configs?: DeleteRequestConfig<P>
  ): Promise<T> => {
    const res = await this.axiosInstance.delete<T>(url, {
      params: configs?.params,
      headers: configs?.headers,
    });

    return res.data;
  };

  private setupErrorHandler = (errorHandler: IErrorHandler) => {
    this.errorInterceptor = this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (e) => {
        // TODO: Show different errors based on the API error
        errorHandler.nonBlockingError('Qualcosa è andato storto!');
        throw e;
      }
    );
  };

  public readonly cancelRequests = () => {
    this.cancelToken.cancel();
  };
}
